import { Link } from '@tanstack/react-router';
import { BookTextIcon, ChevronsUpDown, LogOut, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { Avatar } from '@/components/avatars/avatar';
import { ExternalLink } from '@/components/links/external-link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { pomConfig } from '@/configs/pom-config';
import { cn } from '@/lib/utils';

type NavUserProps = {
  firstname: string;
  lastname: string;
  email: string;
};

export function NavUser({ firstname, lastname, email }: NavUserProps) {
  const { isMobile, state } = useSidebar();
  const { t } = useTranslation('navigation');
  const { signoutRedirect } = useAuth();

  const handleDisconnect = () => {
    signoutRedirect();
  };

  return (
    <SidebarMenu className='z-9999'>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className='hover:bg-sidebar-accent/60 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              size='lg'
            >
              <Avatar
                className={cn(
                  state === 'expanded' ? 'rounded-full' : 'rounded-md pr-1',
                  'size-10',
                )}
                firstname={firstname}
                lastname={lastname}
              />
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>
                  {`${firstname} ${lastname}`}
                </span>
                <span className='truncate text-xs'>{email}</span>
              </div>
              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align='end'
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar firstname={firstname} lastname={lastname} />
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>
                    {`${firstname} ${lastname}`}
                  </span>
                  <span className='truncate text-xs'>{email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <Link className='w-full' to='/settings'>
                <DropdownMenuItem className='cursor-pointer'>
                  <Settings />
                  {t('Preferences')}
                </DropdownMenuItem>
              </Link>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild>
                <ExternalLink
                  className='flex w-full cursor-pointer items-center justify-between gap-2'
                  href={pomConfig.guideUrl}
                >
                  <span className='flex items-center gap-2'>
                    <BookTextIcon />
                    {t('Guide')}
                  </span>
                </ExternalLink>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDisconnect}>
              <LogOut />
              {t('Disconnect')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
